const getAddressComponent = (components, types, useShortName = false) => {
  const requestedTypes = Array.isArray(types) ? types : [types];
  const component = requestedTypes.reduce(
    (match, type) =>
      match ||
      components.find(item => item.types && item.types.includes(type)),
    null
  );

  if (!component) return "";
  return (useShortName ? component.short_name : component.long_name) || "";
};

const toFiniteCoordinate = (value, minimum, maximum) => {
  if (
    value === null ||
    value === undefined ||
    value === "" ||
    (typeof value === "string" && !value.trim())
  ) {
    return null;
  }

  const coordinate = Number(value);
  return Number.isFinite(coordinate) &&
    coordinate >= minimum &&
    coordinate <= maximum
    ? coordinate
    : null;
};

export const hasValidOrganizationCoordinates = profile =>
  Boolean(profile) &&
  toFiniteCoordinate(profile.main_lat, -90, 90) !== null &&
  toFiniteCoordinate(profile.main_long, -180, 180) !== null;

export const parseOrganizationAddressSuggestion = (
  suggestion,
  selectedLocation
) => {
  const components =
    suggestion && suggestion.gmaps && suggestion.gmaps.address_components;
  const location = selectedLocation || (suggestion && suggestion.location);

  if (!Array.isArray(components) || !location) return null;

  const lat = toFiniteCoordinate(location.lat, -90, 90);
  const lng = toFiniteCoordinate(location.lng, -180, 180);
  if (lat === null || lng === null) return null;

  const streetNumber = getAddressComponent(components, "street_number");
  const route = getAddressComponent(components, "route");
  const premise = getAddressComponent(components, ["premise", "establishment"]);
  const streetAddress = [streetNumber, route].filter(Boolean).join(" ");
  const address1 = streetNumber ? streetAddress : premise || streetAddress;

  // A city/postal-code suggestion is useful for search, but is not a complete
  // organization street address and should not replace the saved address.
  if (!address1) return null;

  const postalCode = getAddressComponent(components, "postal_code");
  const postalCodeSuffix = getAddressComponent(
    components,
    "postal_code_suffix"
  );

  return {
    address1,
    address2: getAddressComponent(components, "subpremise"),
    city: getAddressComponent(components, [
      "locality",
      "postal_town",
      "sublocality_level_1",
      "sublocality",
      "administrative_area_level_2"
    ]),
    province: getAddressComponent(
      components,
      "administrative_area_level_1",
      true
    ),
    postalcode:
      postalCode && postalCodeSuffix
        ? `${postalCode}-${postalCodeSuffix}`
        : postalCode,
    country: getAddressComponent(components, "country"),
    main_lat: lat,
    main_long: lng
  };
};

export const getMappableOrganizationLocations = profile => {
  if (!profile) return [];

  const otherLocations = Array.isArray(profile.other_locations)
    ? profile.other_locations
    : [];
  const locations = [
    { lat: profile.main_lat, lng: profile.main_long },
    ...otherLocations
  ];

  return locations.reduce((validLocations, location) => {
    if (
      !location ||
      (location.visible !== undefined && location.visible !== true)
    ) {
      return validLocations;
    }

    const lat = toFiniteCoordinate(location.lat, -90, 90);
    const lng = toFiniteCoordinate(location.lng, -180, 180);
    if (lat === null || lng === null) return validLocations;

    validLocations.push({ ...location, lat, lng });
    return validLocations;
  }, []);
};

export const formatOrganizationAddress = profile => {
  if (!profile) return "";

  return [
    profile.address1,
    profile.address2,
    profile.city,
    profile.province,
    profile.postalcode,
    profile.country
  ]
    .filter(value => typeof value === "string" && value.trim())
    .map(value => value.trim())
    .join(", ");
};

export const hasCompleteOrganizationAddress = profile =>
  Boolean(profile) &&
  ["address1", "city", "province", "postalcode", "country"].every(
    field =>
      typeof profile[field] === "string" && Boolean(profile[field].trim())
  );

const MAPPABLE_GEOCODER_RESULT_TYPES = [
  "street_address",
  "premise",
  "subpremise",
  "establishment",
  "point_of_interest"
];

export const geocodeOrganizationAddress = (
  profile,
  geocoder,
  timeoutMilliseconds = 8000
) =>
  new Promise(resolve => {
    const address = formatOrganizationAddress(profile);
    if (!address || !geocoder || typeof geocoder.geocode !== "function") {
      resolve(null);
      return;
    }

    let completed = false;
    const finish = result => {
      if (completed) return;
      completed = true;
      clearTimeout(timeout);
      resolve(result);
    };
    const timeout = setTimeout(() => finish(null), timeoutMilliseconds);

    try {
      geocoder.geocode({ address }, (results, status) => {
        try {
          if (status !== "OK" || !Array.isArray(results) || !results.length) {
            finish(null);
            return;
          }

          const result = results[0];
          const resultTypes = Array.isArray(result.types) ? result.types : [];
          const isSpecificLocation = resultTypes.some(type =>
            MAPPABLE_GEOCODER_RESULT_TYPES.includes(type)
          );

          // A partial or city/postal-level result can place a program far from
          // its real entrance. Keep the textual fallback instead of guessing.
          if (result.partial_match || !isSpecificLocation) {
            finish(null);
            return;
          }

          const location = result.geometry && result.geometry.location;
          const latitude =
            location && typeof location.lat === "function"
              ? location.lat()
              : location && location.lat;
          const longitude =
            location && typeof location.lng === "function"
              ? location.lng()
              : location && location.lng;
          const main_lat = toFiniteCoordinate(latitude, -90, 90);
          const main_long = toFiniteCoordinate(longitude, -180, 180);

          finish(
            main_lat === null || main_long === null
              ? null
              : { main_lat, main_long }
          );
        } catch (error) {
          finish(null);
        }
      });
    } catch (error) {
      finish(null);
    }
  });
